import React from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ShieldCheck, UserPlus, ClipboardCheck, FlaskConical, HeartPulse,
  CheckCircle2, Stethoscope,
} from "lucide-react";

const STEPS = [
  { icon: UserPlus, step: "01", title: "Connect", body: "An AFHA representative introduces GeneID and answers your questions." },
  { icon: ClipboardCheck, step: "02", title: "Enroll", body: "Complete a simple onboarding form to activate testing for your office." },
  { icon: FlaskConical, step: "03", title: "Order Tests", body: "Collect samples in-office and send them to our CLIA-certified lab." },
  { icon: HeartPulse, step: "04", title: "Guide Patients", body: "Receive clear, actionable reports to inform personalized care." },
];

const BENEFITS = [
  "No lab equipment or upfront cost to your practice",
  "We handle eligibility, insurance & prior-authorizations",
  "Board-certified genetic counselors support your patients",
  "Results delivered to your secure portal in 2–6 weeks",
];

export default function PhysicianOnboarding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="physician-onboarding-page">
      <Navbar />

      {/* HERO */}
      <section className="relative bg-navy-deep text-white overflow-hidden afha-grain">
        <div className="absolute inset-0 stripes-bg opacity-50" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide mb-7">
            <Stethoscope className="h-4 w-4 text-patriot-light" />
            Physician Onboarding
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.03] max-w-4xl">
            Bring GeneID testing into your practice
          </h1>
          <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
            AFHA makes it simple for physicians to offer advanced genetic testing to their
            patients — no lab equipment or upfront cost. Our representatives handle the setup
            so you can focus on care.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button
              onClick={() => navigate("/provider-hub")}
              data-testid="onboarding-hero-cta"
              className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 text-base gap-2"
            >
              Start enrollment <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {BENEFITS.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3.5 py-1.5 text-sm text-white/80">
                <CheckCircle2 className="h-3.5 w-3.5 text-patriot-light" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WATCH FIRST VIDEO */}
      <section className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">Watch first</p>
          <h2 className="font-display text-3xl lg:text-4xl text-navy tracking-tight">Three tailored ways to partner with GeneID</h2>
          <p className="mt-4 text-muted-foreground">A quick overview of how your practice can work with us — then explore the simple four-step process below.</p>
          <div className="mt-8 rounded-2xl overflow-hidden border border-border shadow-lg" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }} data-testid="onboarding-intro-video">
            <iframe
              src="https://player.vimeo.com/video/1207441573?h=3baa12d869&badge=0&autopause=0&player_id=0&app_id=58479"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              title="Three Tailored Ways to Partner with GeneID"
            />
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">How it works</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-navy leading-tight">
            Four simple steps to get started
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            From your first conversation to actionable reports, your team is fully supported at every step.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div
              key={s.step}
              data-testid={`onboarding-step-${i + 1}`}
              className="relative bg-white border border-border rounded-xl p-7 hover:border-navy hover:shadow-[0_8px_30px_rgba(15,23,42,0.08)] transition-all duration-300"
            >
              <span className="absolute top-6 right-6 font-display text-3xl font-black text-navy/10">{s.step}</span>
              <div className="h-12 w-12 rounded-lg bg-navy/5 flex items-center justify-center mb-5">
                <s.icon className="h-6 w-6 text-navy" />
              </div>
              <h3 className="font-display text-xl text-navy mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-navy-deep text-white rounded-xl p-8 afha-grain flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl">Ready to offer GeneID testing?</h3>
            <p className="text-white/70 mt-1 text-sm">Explore the Provider Hub and complete a secure enrollment form to activate testing for your office.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button
              onClick={() => navigate("/provider-hub")}
              data-testid="onboarding-cta"
              className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2"
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => navigate("/provider-hub")}
              variant="outline"
              data-testid="onboarding-provider-hub"
              className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-navy h-12 px-8 gap-2"
            >
              Visit Provider Hub <ShieldCheck className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
